import { Link } from 'react-router-dom';
import { useState } from 'react';

const NEWSLETTER_FORM_URL = 'https://formspree.io/f/meewdjbl';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: new URLSearchParams({ email, _subject: 'Boletín — Toldos Málaga', formulario: 'Boletín' }),
      });
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      console.error('Error al suscribirse:', err);
    } finally {
      setLoading(false);
    }
  };

  const serviceLinks = [
    { to: '/reparacion-toldos-malaga', label: 'Reparación de toldos' },
    { to: '/instalacion-toldos-malaga', label: 'Instalación a medida' },
    { to: '/motorizacion-toldos-malaga', label: 'Motorización' },
    { to: '/mantenimiento-toldos-malaga', label: 'Mantenimiento' },
  ];

  const guideLinks = [
    { to: '/guias/toldo-no-recoge', label: 'Toldo no recoge' },
    { to: '/guias/brazo-toldo-roto', label: 'Brazo de toldo roto' },
    { to: '/guias/cambiar-lona-toldo', label: 'Cambiar lona o tela' },
    { to: '/guias/toldo-descolgado', label: 'Toldo descolgado' },
    { to: '/guias/cambiar-motor-toldo', label: 'Cambiar motor' },
  ];

  const infoLinks = [
    { to: '/toldos-hosteleria', label: 'Toldos para hostelería' },
    { to: '/presupuesto', label: 'Solicitar presupuesto' },
    { to: '/contacto', label: 'Contacto' },
  ];

  return (
    <footer className="bg-[#0F5066] text-white">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-16">
        {/* Top Bar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pb-10 border-b border-white/15">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src="/logo-footer.svg"
              alt="Toldos Málaga - Reparación e Instalación de Toldos"
              title="Toldos Málaga"
              className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>
          <p className="text-white/60 text-sm max-w-md">
            Tolderos profesionales en Málaga. Reparación rápida, instalación a medida y mantenimiento de toldos para hogares y negocios.
          </p>
        </div>

        {/* Link Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-10">
          {/* Servicios */}
          <div>
            <h4 className="text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">Servicios</h4>
            <ul className="space-y-2.5">
              {serviceLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-white/65 hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Guías */}
          <div>
            <h4 className="text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">Guías</h4>
            <ul className="space-y-2.5">
              {guideLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-white/65 hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Información */}
          <div>
            <h4 className="text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">Información</h4>
            <ul className="space-y-2.5">
              {infoLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-white/65 hover:text-accent-400 transition-colors duration-200 whitespace-nowrap">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">Boletín</h4>
            <p className="text-sm text-white/55 mb-3">
              Consejos de mantenimiento y ofertas exclusivas.
            </p>
            {submitted ? (
              <p className="text-sm text-accent-400 font-medium">
                ¡Gracias por suscribirte!
              </p>
            ) : (
              <form onSubmit={handleNewsletter} className="flex flex-col gap-2" data-readdy-form="newsletter-toldos-malaga">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-white/40 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2.5 rounded-lg bg-accent-500 text-foreground-900 text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {loading ? 'Enviando...' : 'Suscribirse'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-white/15">
          <div className="flex flex-col gap-1">
            <p className="text-sm text-white/50">
              &copy; {new Date().getFullYear()} Toldos Málaga. Todos los derechos reservados.
            </p>
            <p className="text-xs text-white/35">
              ¿Buscas persianas? Visita{' '}
              <a href="https://persianas-malaga.es" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-400 transition-colors">
                persianas-malaga.es
              </a>
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+34951120770" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 hover:text-foreground-900 text-white/70 transition-all duration-200" aria-label="Teléfono">
              <i className="ri-phone-line text-lg"></i>
            </a>
            <a href="https://wa.me/34627213347" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 hover:text-foreground-900 text-white/70 transition-all duration-200" aria-label="WhatsApp">
              <i className="ri-whatsapp-line text-lg"></i>
            </a>
            <a href="mailto:info@toldos-malaga.es" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-accent-500 hover:text-foreground-900 text-white/70 transition-all duration-200" aria-label="Email">
              <i className="ri-mail-line text-lg"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}